/**
 * useManualApprovalPendingCount — sidebar badge poller for Kodia Plan 05 §6.2.
 *
 * Polls `GET /loyalty/admin/manual-approval/pending-count` every 60 s. Silently
 * disables itself on 403/404 (the module isn't enabled for this tenant) so
 * that Oreno/Sudama don't spam the network with dead requests.
 *
 * Consumers:
 *   const { count, enabled } = useManualApprovalPendingCount();
 *   // Render a badge when `enabled && count > 0`.
 */

import { useEffect, useRef, useState } from 'react';
import { manualApprovalAdminService } from '../services/loyalty.service';

const POLL_INTERVAL_MS = 60_000;

let cachedCount: number | null = null;

export interface ManualApprovalPendingCountState {
  count: number;
  loading: boolean;
  /** false once the endpoint returns 403/404 — badge should stay hidden. */
  enabled: boolean;
}

export const useManualApprovalPendingCount = (
  active: boolean = true,
): ManualApprovalPendingCountState => {
  const [count, setCount] = useState<number>(cachedCount ?? 0);
  const [loading, setLoading] = useState<boolean>(false);
  const [enabled, setEnabled] = useState<boolean>(true);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;

    const fetchOnce = async () => {
      if (cancelled || !enabled) return;
      setLoading(true);
      try {
        const res = await manualApprovalAdminService.getPendingCount();
        if (cancelled) return;
        const next = res?.count ?? 0;
        cachedCount = next;
        setCount(next);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
          // Module isn't wired for this tenant — stop polling permanently.
          setEnabled(false);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
        // Any other error: keep the previous count, silently retry next tick.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Initial fetch + interval.
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, enabled]);

  return { count, loading, enabled };
};

export default useManualApprovalPendingCount;
